import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiClose, mdiCheckCircleOutline } from '@mdi/js';
import { CircuitSchema } from '../types';
import { apiService } from '../services/apiService';
import { useEscapeKey } from '../hooks/useEscapeKey';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e8eaed;
`;

const HeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #5f6368;
  border-radius: 4px;

  &:hover {
    background: #f1f3f4;
  }
`;

const FormBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  margin-bottom: 4px;
`;

const RequiredMark = styled.span`
  color: #ea4335;
  margin-left: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #202124;
  border: 1px solid #dadce0;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
  }

  &::placeholder {
    color: #9aa0a6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #202124;
  border: 1px solid #dadce0;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
  }

  &::placeholder {
    color: #9aa0a6;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #e8eaed;
  gap: 8px;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #5f6368;
  background: none;
  border: 1px solid #dadce0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #f1f3f4;
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  background: ${props => props.disabled ? '#9aa0a6' : '#1a73e8'};
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover:not(:disabled) {
    background: #1765cc;
  }
`;

const ErrorMessage = styled.div`
  background: #fce8e6;
  color: #c5221f;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 14px;
`;

const SuccessMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e6f4ea;
  color: #34a853;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const SuccessTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  margin: 0 0 8px;
`;

const SuccessText = styled.p`
  font-size: 13px;
  color: #5f6368;
  margin: 0;
  line-height: 1.5;
`;

interface RfqModalProps {
  schema: CircuitSchema;
  onClose: () => void;
}

export const RfqModal: React.FC<RfqModalProps> = ({ schema, onClose }) => {
  useEscapeKey(onClose);

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = name.trim() && company.trim() && email.trim() && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError(null);

    try {
      await apiService.submitRfq({
        name: name.trim(),
        company: company.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
        circuit_schema: schema,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTitle>Request for Quote</HeaderTitle>
          <CloseButton onClick={onClose}>
            <Icon path={mdiClose} size={0.7} />
          </CloseButton>
        </Header>

        {submitted ? (
          <FormBody>
            <SuccessMessage>
              <SuccessIcon><Icon path={mdiCheckCircleOutline} size={1.2} /></SuccessIcon>
              <SuccessTitle>Request Submitted</SuccessTitle>
              <SuccessText>
                Thank you for your inquiry. Our team will review your circuit
                configuration and get back to you shortly.
              </SuccessText>
            </SuccessMessage>
          </FormBody>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormBody>
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <FormGroup>
                <Label>
                  Name<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Company<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Email<RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </FormGroup>

              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Additional details about your requirements..."
                />
              </FormGroup>
            </FormBody>

            <Footer>
              <CancelButton type="button" onClick={onClose}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={!canSubmit}>
                {loading ? 'Submitting...' : 'Submit Request'}
              </SubmitButton>
            </Footer>
          </form>
        )}
      </Panel>
    </Overlay>
  );
};
